import React, { useEffect, useState } from 'react';
import {
    View,
    Text,
    StyleSheet,
    StatusBar,
    TouchableOpacity,
    Image,
    Platform,
} from 'react-native';
import type { V2Balance, V2CardDetailResponse, V2Request } from '../../types/v2';
import TabBar from '../../components/TabBar';
import { COLORS } from '../../theme/themeTokens';
import { v2GetCard, v2GetMyBalances } from '../../services/v2/api';
import ConfirmDepositScreen from '../Account/Deposit/ConfirmDepositScreen';
import WithdrawScreen from '../Account/Withdraw/WithdrawScreen';
import DepositCardScreen from '../Card/DepositCardScreen';
import ExchangeScreen, { ExchangePreset } from '../Account/Exchange/ExchangeScreen';
import NoCardScreen from '../Card/NoCardScreen';

export type OperationsTab = 'inflow' | 'withdraw' | 'topup' | 'exchange';

interface OperationsScreenProps {
    /** Initial tab to focus when the screen opens. */
    initialTab: OperationsTab;
    /** Balance used by Inflow and Withdraw tabs (current-account scope). */
    balance: V2Balance;
    /** Balance used by Top-up tab (card scope). Optional — when missing, the
     *  Top-up tab renders a placeholder. */
    cardBalance: V2Balance | null;
    cardDetail: V2CardDetailResponse | null;
    onBack: () => void;
    onNavigate: (screen: string) => void;
    // Withdraw-tab specific pass-through props.
    onTransferSuccess?: () => void;
    onScanQR?: () => void;
    scannedAddress?: string | null;
    onClearScannedAddress?: () => void;
    withdrawPersistedNetworkId?: string | null;
    onWithdrawNetworkChange?: (networkId: string) => void;
    // Top-up-tab specific.
    onTopUpSuccess?: () => void;
    /** Fired AFTER NoCardScreen successfully submits a card application
     *  (PIN flow handled internally). Parent typically navigates back to
     *  the dashboard. */
    onApplyCardSubmitted?: () => void;
    /** Completed order whose values should be copied into the selected form. */
    repeatRequest?: V2Request | null;
    /** Token and price pre-filled into the Exchange tab (xStock Buy/Sell). */
    exchangePreset?: ExchangePreset | null;
}

/**
 * Single-screen wrapper that hosts three financial flows as keep-alive tabs.
 * The parent (App.tsx) replaces three legacy tabs (`confirmDeposit` / `withdraw`
 * / `depositCard`) with this one and picks the initial tab via `initialTab`.
 *
 * Each tab renders the existing screen in `embedded` mode (no inner header /
 * tab bar) so this container can supply a single header + TabBar.
 *
 * Tabs are mounted lazily on first activation and kept mounted afterwards via
 * `display: 'none'` toggling — preserves user-entered form state when they
 * switch tabs and come back.
 */
const OperationsScreen: React.FC<OperationsScreenProps> = ({
    initialTab,
    balance,
    cardBalance,
    cardDetail,
    onBack,
    onNavigate,
    onTransferSuccess,
    onScanQR,
    scannedAddress,
    onClearScannedAddress,
    withdrawPersistedNetworkId,
    onWithdrawNetworkChange,
    onTopUpSuccess,
    onApplyCardSubmitted,
    repeatRequest,
    exchangePreset,
}) => {
    // Matches CardScreen so Operations lines up vertically with the
    // rest of the in-app screens. iOS gets 0 because the App-level
    // SafeAreaView already handles the notch; Android keeps 28 to
    // clear its status bar.
    const statusBarHeight = Platform.OS === 'android' ? 28 : 0;
    const [activeTab, setActiveTab] = useState<OperationsTab>(initialTab);

    // Lazy keep-alive: mount a tab the first time it's activated; never
    // unmount afterwards. Re-mounting WithdrawScreen / DepositCardScreen
    // would wipe their internal form state — that's the UX we want to avoid.
    const [mounted, setMounted] = useState<Record<OperationsTab, boolean>>({
        inflow: initialTab === 'inflow',
        withdraw: initialTab === 'withdraw',
        topup: initialTab === 'topup',
        exchange: initialTab === 'exchange',
    });

    useEffect(() => {
        setMounted((prev) =>
            prev[activeTab] ? prev : { ...prev, [activeTab]: true },
        );
    }, [activeTab]);

    // Card availability check for the Top-up tab. Two sequential stages:
    //   1. Does the user own a card balance? (cardBalance prop, or fallback
    //      to v2GetMyBalances filtered by account_type_code = card/card_balance)
    //   2. Is that card NOT blocked in Payoma? (v2GetCard → data.blocked)
    // Either failure → render NoCardScreen. Both pass → DepositCardScreen.
    // Single 'pending' phase covers both stages so the user sees one
    // "Loading…" placeholder for the whole check, never a flash of
    // NoCardScreen before the verdict is known.
    type CardCheckState =
        | { phase: 'pending' }
        | { phase: 'ok'; balance: V2Balance }
        | { phase: 'fail' };
    const [cardCheck, setCardCheck] = useState<CardCheckState>({ phase: 'pending' });

    useEffect(() => {
        // Re-runs only while the verdict is still pending — once it
        // resolves to 'ok' or 'fail' the guard short-circuits all
        // subsequent renders. This intentionally allows a retry when
        // the first attempt got cancelled mid-flight (e.g. the user
        // switched away from the Top-up tab before the API replied) —
        // the previous in-flight request is cancelled via `cancelled`
        // below, and the next render kicks off a fresh fetch.
        if (cardCheck.phase !== 'pending') return;
        if (activeTab !== 'topup' && !mounted.topup) return;
        let cancelled = false;
        (async () => {
            try {
                // Stage 1 — pick a card_balance candidate. Use the prop when
                // App.tsx already resolved one; otherwise hit /user/balances.
                let candidate: V2Balance | null = cardBalance ?? null;
                if (!candidate) {
                    const res = await v2GetMyBalances();
                    candidate = (res.balances ?? []).find(
                        (b) =>
                            b.account_type_code === 'card' ||
                            b.account_type_code === 'card_balance',
                    ) ?? null;
                }
                if (cancelled) return;
                if (!candidate || !candidate.wallet_address) {
                    setCardCheck({ phase: 'fail' });
                    return;
                }

                // Stage 2 — Payoma must report the card as not blocked. The
                // wallet_address on a card_balance row is the Payoma card UUID,
                // accepted by GET /api/v2/user/cards/:id.
                const detail = await v2GetCard(candidate.wallet_address);
                if (cancelled) return;
                if (detail?.data?.blocked === true) {
                    setCardCheck({ phase: 'fail' });
                    return;
                }
                setCardCheck({ phase: 'ok', balance: candidate });
            } catch (err) {
                // Network failure on either stage → fail closed. Better to
                // surface NoCardScreen than land the user on a Top-up form
                // whose submit is guaranteed to fail downstream.
                console.warn('[OperationsScreen] card check failed:', err);
                if (!cancelled) {
                    setCardCheck({ phase: 'fail' });
                }
            }
        })();
        return () => { cancelled = true; };
    }, [activeTab, mounted.topup, cardBalance, cardCheck.phase]);

    const renderTabButton = (tab: OperationsTab, label: string) => {
        const isActive = activeTab === tab;
        return (
            <TouchableOpacity
                key={tab}
                style={styles.tabButton}
                onPress={() => setActiveTab(tab)}
                activeOpacity={0.7}
            >
                <Text style={[styles.tabLabel, isActive && styles.tabLabelActive]}>
                    {label}
                </Text>
                <View
                    style={[
                        styles.tabUnderline,
                        isActive && styles.tabUnderlineActive,
                    ]}
                />
            </TouchableOpacity>
        );
    };

    return (
        <View style={styles.container}>
            <StatusBar barStyle="light-content" backgroundColor={COLORS.primaryNavy} />

            {/* Custom navy header — 44 dp content row pinned to spec.
                Whole row is tappable as Back, same pattern as
                ReportsScreen / NotificationsScreen. SimpleHeader was
                here before but its height varied with the shared
                component's paddings; we needed exact 44 + 12-dp gap +
                44-dp tab strip per the design. */}
            <View style={[styles.headerBackground, { paddingTop: statusBarHeight }]}>
                <TouchableOpacity
                    style={styles.headerRow}
                    activeOpacity={0.7}
                    onPress={onBack}
                >
                    <View style={styles.backButton}>
                        <Image
                            source={require('../../assets/arrow_left_20_white.png')}
                            style={styles.backIcon}
                            resizeMode="contain"
                        />
                    </View>
                    <Text style={styles.headerTitle}>Operations</Text>
                    <View style={styles.backButton} />
                </TouchableOpacity>
            </View>

            {/* 12-dp gap between the header and the tab strip. */}
            <View style={styles.headerGap} />

            <View style={styles.tabStrip}>
                {renderTabButton('inflow', 'Inflow')}
                {renderTabButton('withdraw', 'Withdraw')}
                {renderTabButton('topup', 'Top-up')}
                {renderTabButton('exchange', 'Exchange')}
            </View>

            <View
                style={[
                    styles.tabPane,
                    activeTab !== 'inflow' && styles.tabPaneHidden,
                ]}
            >
                {mounted.inflow && (
                    <ConfirmDepositScreen
                        embedded
                        balance={balance}
                        onBack={onBack}
                        onNavigate={onNavigate}
                    />
                )}
            </View>

            <View
                style={[
                    styles.tabPane,
                    activeTab !== 'withdraw' && styles.tabPaneHidden,
                ]}
            >
                {mounted.withdraw && (
                    <WithdrawScreen
                        embedded
                        balance={balance}
                        onBack={onBack}
                        onNavigate={onNavigate}
                        onTransferSuccess={onTransferSuccess}
                        onScanQR={onScanQR}
                        scannedAddress={scannedAddress}
                        onClearScannedAddress={onClearScannedAddress}
                        persistedNetworkId={withdrawPersistedNetworkId}
                        onNetworkChange={onWithdrawNetworkChange}
                        repeatPrefill={repeatRequest ? {
                            balanceId: repeatRequest.balance_id,
                            destinationAddress: repeatRequest.destination_address,
                            amount: repeatRequest.amount,
                        } : undefined}
                    />
                )}
            </View>

            <View
                style={[
                    styles.tabPane,
                    activeTab !== 'topup' && styles.tabPaneHidden,
                ]}
            >
                {mounted.topup && (
                    cardCheck.phase === 'pending' ? (
                        <View style={styles.placeholder}>
                            <Text style={styles.placeholderText}>Loading…</Text>
                        </View>
                    ) : cardCheck.phase === 'ok' ? (
                        <DepositCardScreen
                            embedded
                            cardBalance={cardCheck.balance}
                            cardDetail={cardDetail}
                            onBack={onBack}
                            onNavigate={onNavigate}
                            onTransferSuccess={onTopUpSuccess ?? onBack}
                            repeatPrefill={repeatRequest ? {
                                sourceBalanceId: repeatRequest.balance_id,
                                destinationBalanceId: repeatRequest.destination_balance_id,
                                amount: repeatRequest.amount,
                                destinationAmount: repeatRequest.destination_amount,
                            } : undefined}
                        />
                    ) : (
                        // 'fail' — either no card_balance row exists or
                        // Payoma reports the card as blocked. Single
                        // NoCardScreen surface for both — see plan v3.
                        <NoCardScreen
                            embedded
                            onBack={onBack}
                            onNavigate={onNavigate}
                            onSubmitted={onApplyCardSubmitted ?? onBack}
                        />
                    )
                )}
            </View>

            <View
                style={[
                    styles.tabPane,
                    activeTab !== 'exchange' && styles.tabPaneHidden,
                ]}
            >
                {mounted.exchange && (
                    <ExchangeScreen
                        embedded
                        onBack={onBack}
                        onNavigate={onNavigate}
                        preset={exchangePreset}
                    />
                )}
            </View>

            <TabBar activeTab="dashboard" onNavigate={onNavigate} />
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: COLORS.primaryNavy,
    },
    /* Header strip — fixed 44-dp content row (matches ReportsScreen). */
    headerBackground: {
        backgroundColor: COLORS.primaryNavy,
    },
    headerRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 16,
        height: 44,
    },
    backButton: {
        width: 40,
        height: 40,
        alignItems: 'center',
        justifyContent: 'center',
    },
    backIcon: {
        width: 20,
        height: 20,
        tintColor: COLORS.white,
    },
    headerTitle: {
        color: COLORS.white,
        fontSize: 20,
        fontWeight: '600',
        textAlign: 'center',
    },
    /* 12-dp navy gap between header and tab strip. */
    headerGap: {
        height: 12,
        backgroundColor: COLORS.primaryNavy,
    },
    /* Tab strip — fixed 44-dp height. Label pinned to bottom of each
       button so the active-tab underline sits flush against the strip's
       bottom edge. */
    tabStrip: {
        flexDirection: 'row',
        backgroundColor: COLORS.primaryNavy,
        paddingHorizontal: 20,
        height: 44,
    },
    tabButton: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'flex-end',
    },
    tabLabel: {
        fontFamily: 'Inter',
        fontSize: 16,
        fontWeight: '400',
        color: COLORS.blueSteel,
        paddingBottom: 8,
    },
    tabLabelActive: {
        color: COLORS.white,
        fontSize: 18,
        fontWeight: '500',
    },
    tabUnderline: {
        height: 2,
        width: '100%',
        backgroundColor: 'transparent',
    },
    tabUnderlineActive: {
        backgroundColor: COLORS.brandOrange,
    },
    tabPane: {
        flex: 1,
    },
    tabPaneHidden: {
        display: 'none',
    },
    placeholder: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: COLORS.pureLight,
    },
    placeholderText: {
        fontFamily: 'Inter',
        fontSize: 16,
        color: COLORS.blueSteel,
    },
});

export default OperationsScreen;
